import { createHashHistory } from 'history';
import { isAction } from 'redux';
import type { AnyAction, Middleware, Reducer, Store } from 'redux';

export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

export interface RouterLocation {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
  key?: string;
}

type HistoryAction = 'POP' | 'PUSH' | 'REPLACE';
type LocationDescriptor = string | Partial<RouterLocation>;
type PushArguments = [location: LocationDescriptor, state?: unknown];

interface RouterHistory {
  action: HistoryAction;
  location: RouterLocation;
  listen(
    listener: (update: {
      location: RouterLocation;
      action: HistoryAction;
    }) => void
  ): () => void;
  push(...args: PushArguments): void;
  replace(...args: PushArguments): void;
}

interface LocationChangeAction extends AnyAction {
  type: typeof LOCATION_CHANGE;
  payload: {
    location: RouterLocation;
    action: HistoryAction;
    isFirstRendering: boolean;
  };
}

interface PushAction extends AnyAction {
  type: typeof CALL_HISTORY_METHOD;
  payload: {
    method: 'push';
    args: PushArguments;
  };
}

interface NavigateBackAction extends AnyAction {
  type: typeof CALL_HISTORY_METHOD;
  payload: {
    method: 'back';
    fallback: LocationDescriptor;
  };
}

export interface RouterState {
  location: RouterLocation;
  action: HistoryAction;
  previousLocation?: RouterLocation;
}

export const history = createHashHistory() as unknown as RouterHistory;

// history@4 normalized an empty hash to `#/` during initialization. Preserve
// that URL contract without adding another entry to the browser history.
if (!window.location.hash) {
  history.replace('/');
}

export function push(...args: PushArguments): PushAction {
  return {
    type: CALL_HISTORY_METHOD,
    payload: { method: 'push', args },
  };
}

export function navigateBack(
  fallback: LocationDescriptor = '/'
): NavigateBackAction {
  return {
    type: CALL_HISTORY_METHOD,
    payload: { method: 'back', fallback },
  };
}

function pushLocation([location, state]: PushArguments): void {
  if (typeof location === 'string' || state !== undefined) {
    history.push(location, state);
    return;
  }

  // history@4 accepted state as part of a location descriptor. history@5
  // expects it as the second argument and otherwise replaces it with null.
  const { state: locationState, key: _key, ...path } = location;
  history.push(path, locationState);
}

function locationChanged(
  location: RouterLocation,
  action: HistoryAction,
  isFirstRendering = false
): LocationChangeAction {
  return {
    type: LOCATION_CHANGE,
    payload: { location, action, isFirstRendering },
  };
}

const initialState: RouterState = {
  location: history.location,
  action: history.action,
};

export const routerReducer: Reducer<RouterState, AnyAction> = (
  state = initialState,
  action
) => {
  if (action.type !== LOCATION_CHANGE) {
    return state;
  }

  const {
    location,
    action: historyAction,
    isFirstRendering,
  } = (action as LocationChangeAction).payload;

  if (isFirstRendering) {
    return state;
  }

  return {
    location,
    action: historyAction,
    previousLocation:
      historyAction === 'PUSH' ? state.location : state.previousLocation,
  };
};

export const routerMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    if (!isAction(action) || action.type !== CALL_HISTORY_METHOD) {
      return next(action);
    }

    const navigationAction = action as PushAction | NavigateBackAction;
    if (navigationAction.payload.method === 'back') {
      const { previousLocation } = (getState() as { router: RouterState })
        .router;
      pushLocation([
        previousLocation ?? navigationAction.payload.fallback,
      ]);
      return;
    }

    pushLocation(navigationAction.payload.args);
  };

export function getLocation(state: { router: RouterState }): RouterLocation {
  return state.router.location;
}

export function startRouterListener(
  store: Pick<Store, 'dispatch'>
): () => void {
  const unlisten = history.listen(({ location, action }) => {
    store.dispatch(locationChanged(location, action));
  });

  store.dispatch(locationChanged(history.location, history.action, true));
  return unlisten;
}
